export const RELATIONSHIP_RISK_SETTINGS_COLLECTION = "relationshipRiskSettings";
export const RELATIONSHIP_RISK_DEFAULT_DOC_ID = "lists";

export const RELATIONSHIP_RISK_MODEL_3_LEVEL = "standard_medium_high";
export const RELATIONSHIP_RISK_MODEL_2_LEVEL = "standard_high";

export const RELATIONSHIP_RISK_LEVEL_STANDARD = "standard";
export const RELATIONSHIP_RISK_LEVEL_MEDIUM = "medium";
export const RELATIONSHIP_RISK_LEVEL_HIGH = "high";

export const RELATIONSHIP_RISK_MODEL_OPTIONS = [
  {
    key: RELATIONSHIP_RISK_MODEL_3_LEVEL,
    label: "3 levels",
    detail: "Standard / Medium / High",
  },
  {
    key: RELATIONSHIP_RISK_MODEL_2_LEVEL,
    label: "2 levels",
    detail: "Standard / High",
  },
];

export const RELATIONSHIP_RISK_LEVELS_BY_MODEL = {
  [RELATIONSHIP_RISK_MODEL_3_LEVEL]: [
    { key: RELATIONSHIP_RISK_LEVEL_STANDARD, label: "Standard risk" },
    { key: RELATIONSHIP_RISK_LEVEL_MEDIUM, label: "Medium risk" },
    { key: RELATIONSHIP_RISK_LEVEL_HIGH, label: "High risk" },
  ],
  [RELATIONSHIP_RISK_MODEL_2_LEVEL]: [
    { key: RELATIONSHIP_RISK_LEVEL_STANDARD, label: "Standard risk" },
    { key: RELATIONSHIP_RISK_LEVEL_HIGH, label: "High risk" },
  ],
};

export const DEFAULT_RELATIONSHIP_RISK_RULES_BY_MODEL = {
  [RELATIONSHIP_RISK_MODEL_3_LEVEL]: {
    model: RELATIONSHIP_RISK_MODEL_3_LEVEL,
    factors: {
      industryHigh: { impact: "immediate_high" },
      industryMedium: { impact: "medium" },
      geoHigh: { impact: "immediate_high" },
      geoMedium: { impact: "medium" },
      pepExposure: { impact: "immediate_high" },
      sanctionsExposure: { impact: "immediate_high" },
      negativeNews: { impact: "immediate_high" },
      complexStructure: { impact: "medium" },
      notMetInPerson: { impact: "medium" },
    },
    thresholds: {
      mediumFactorsToMediumEnabled: true,
      mediumFactorsToMedium: 1,
      mediumFactorsToHighEnabled: true,
      mediumFactorsToHigh: 2,
      combinedFactorsToHigh: {
        enabled: false,
        highFactors: 1,
        mediumFactors: 2,
      },
      highFactorsToHighEnabled: true,
      highFactorsToHigh: 2,
    },
  },
  [RELATIONSHIP_RISK_MODEL_2_LEVEL]: {
    model: RELATIONSHIP_RISK_MODEL_2_LEVEL,
    factors: {
      industryHigh: { impact: "immediate_high" },
      industryMedium: { impact: "medium" },
      geoHigh: { impact: "immediate_high" },
      geoMedium: { impact: "medium" },
      pepExposure: { impact: "immediate_high" },
      sanctionsExposure: { impact: "immediate_high" },
      negativeNews: { impact: "immediate_high" },
      complexStructure: { impact: "medium" },
      notMetInPerson: { impact: "medium" },
    },
    thresholds: {
      mediumFactorsToMediumEnabled: false,
      mediumFactorsToMedium: 1,
      mediumFactorsToHighEnabled: true,
      mediumFactorsToHigh: 2,
      combinedFactorsToHigh: {
        enabled: false,
        highFactors: 1,
        mediumFactors: 2,
      },
      highFactorsToHighEnabled: true,
      highFactorsToHigh: 2,
    },
  },
};

export const DEFAULT_RELATIONSHIP_RISK_SETTINGS = {
  activeModel: RELATIONSHIP_RISK_MODEL_3_LEVEL,
  models: DEFAULT_RELATIONSHIP_RISK_RULES_BY_MODEL,
};

export const RELATIONSHIP_RISK_IMPACTS_BY_MODEL = {
  [RELATIONSHIP_RISK_MODEL_3_LEVEL]: [
    { key: "none", label: "Not used" },
    { key: "medium", label: "Medium factor" },
    { key: "immediate_medium", label: "Immediate Medium" },
    { key: "high", label: "High factor" },
    { key: "immediate_high", label: "Immediate High" },
  ],
  [RELATIONSHIP_RISK_MODEL_2_LEVEL]: [
    { key: "none", label: "Not used" },
    { key: "medium", label: "Risk factor" },
    { key: "high", label: "High factor" },
    { key: "immediate_high", label: "Immediate High" },
  ],
};

export const RELATIONSHIP_RISK_FACTOR_OPTIONS = [
  { key: "industryHigh", label: "High risk business activity" },
  { key: "industryMedium", label: "Medium risk business activity" },
  { key: "geoHigh", label: "High risk country" },
  { key: "geoMedium", label: "Medium risk country" },
  { key: "pepExposure", label: "PEP exposure" },
  { key: "sanctionsExposure", label: "Sanctions exposure" },
  { key: "negativeNews", label: "Negative news" },
  { key: "complexStructure", label: "Complex ownership structure" },
  { key: "notMetInPerson", label: "No meeting with contracting partner" },
];

const FACTOR_REASON_LABELS = Object.fromEntries(
  RELATIONSHIP_RISK_FACTOR_OPTIONS.map((option) => [option.key, option.label])
);

export function normalizeRelationshipRiskModel(value) {
  return value === RELATIONSHIP_RISK_MODEL_2_LEVEL
    ? RELATIONSHIP_RISK_MODEL_2_LEVEL
    : RELATIONSHIP_RISK_MODEL_3_LEVEL;
}

export function relationshipRiskLevelsForModel(model) {
  return RELATIONSHIP_RISK_LEVELS_BY_MODEL[normalizeRelationshipRiskModel(model)];
}

export function relationshipRiskImpactsForModel(model) {
  return RELATIONSHIP_RISK_IMPACTS_BY_MODEL[normalizeRelationshipRiskModel(model)];
}

export function relationshipRiskFactorsForModel() {
  return RELATIONSHIP_RISK_FACTOR_OPTIONS;
}

export function normalizeRelationshipRiskImpact(value) {
  const v = String(value || "").toLowerCase();
  if (
    v === "immediate_high" ||
    v === "high" ||
    v === "immediate_medium" ||
    v === "medium" ||
    v === "none"
  ) {
    return v;
  }
  return "none";
}

export function normalizeRelationshipRiskImpactForModel(value, model) {
  const impact = normalizeRelationshipRiskImpact(value);
  const allowed = new Set(relationshipRiskImpactsForModel(model).map((item) => item.key));
  if (allowed.has(impact)) return impact;
  if (impact === "immediate_medium" && allowed.has("medium")) return "medium";
  return "none";
}

export function normalizeRelationshipRiskLevel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("high")) return RELATIONSHIP_RISK_LEVEL_HIGH;
  if (raw.includes("medium")) return RELATIONSHIP_RISK_LEVEL_MEDIUM;
  if (raw.includes("standard") || raw.includes("low")) {
    return RELATIONSHIP_RISK_LEVEL_STANDARD;
  }
  return "";
}

export function relationshipRiskLabel(level) {
  const clean = normalizeRelationshipRiskLevel(level);
  if (clean === RELATIONSHIP_RISK_LEVEL_HIGH) return "High";
  if (clean === RELATIONSHIP_RISK_LEVEL_MEDIUM) return "Medium";
  if (clean === RELATIONSHIP_RISK_LEVEL_STANDARD) return "Standard";
  return "—";
}

export function relationshipRiskTone(level) {
  const clean = normalizeRelationshipRiskLevel(level);
  if (clean === RELATIONSHIP_RISK_LEVEL_HIGH) return "high";
  if (clean === RELATIONSHIP_RISK_LEVEL_MEDIUM) return "medium";
  if (clean === RELATIONSHIP_RISK_LEVEL_STANDARD) return "low";
  return "unknown";
}

export function normalizeSourceRiskLevel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "high") return "high";
  if (raw === "medium") return "medium";
  if (raw === "standard" || raw === "low") return "standard";
  return "";
}

export function sanitizeRelationshipRiskRules(raw, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const cleanModel = normalizeRelationshipRiskModel(model);
  const src = raw || {};
  const factors = {};
  const defaultRules = DEFAULT_RELATIONSHIP_RISK_RULES_BY_MODEL[cleanModel];

  RELATIONSHIP_RISK_FACTOR_OPTIONS.forEach((option) => {
    const directImpact = src.factors?.[option.key]?.impact;
    const defaultImpact = defaultRules.factors[option.key]?.impact || "none";
    const impact = directImpact === undefined ? defaultImpact : directImpact;

    factors[option.key] = {
      impact: normalizeRelationshipRiskImpactForModel(impact, cleanModel),
    };
  });

  const factorCount = RELATIONSHIP_RISK_FACTOR_OPTIONS.length;
  const srcThresholds = src.thresholds || {};
  const defaultThresholds = defaultRules.thresholds;
  const mediumFactorsToHigh = Math.min(
    Math.max(1, Number(srcThresholds.mediumFactorsToHigh) || defaultThresholds.mediumFactorsToHigh),
    factorCount
  );
  const mediumFactorsToMedium = Math.min(
    Math.max(1, Number(srcThresholds.mediumFactorsToMedium) || defaultThresholds.mediumFactorsToMedium),
    mediumFactorsToHigh
  );
  const highFactorsToHigh = Math.min(
    Math.max(1, Number(srcThresholds.highFactorsToHigh) || defaultThresholds.highFactorsToHigh),
    factorCount
  );
  const combinedSource = srcThresholds.combinedFactorsToHigh || {};
  const combinedDefault = defaultThresholds.combinedFactorsToHigh;

  return {
    model: cleanModel,
    factors,
    thresholds: {
      mediumFactorsToMediumEnabled:
        cleanModel === RELATIONSHIP_RISK_MODEL_2_LEVEL
          ? false
          : srcThresholds.mediumFactorsToMediumEnabled === undefined
          ? defaultThresholds.mediumFactorsToMediumEnabled
          : srcThresholds.mediumFactorsToMediumEnabled === true,
      mediumFactorsToMedium,
      mediumFactorsToHighEnabled:
        srcThresholds.mediumFactorsToHighEnabled === undefined
          ? defaultThresholds.mediumFactorsToHighEnabled
          : srcThresholds.mediumFactorsToHighEnabled === true,
      mediumFactorsToHigh,
      combinedFactorsToHigh: {
        enabled: combinedSource.enabled === undefined ? combinedDefault.enabled : combinedSource.enabled === true,
        highFactors: Math.min(
          Math.max(1, Number(combinedSource.highFactors) || combinedDefault.highFactors),
          factorCount
        ),
        mediumFactors: Math.min(
          Math.max(1, Number(combinedSource.mediumFactors) || combinedDefault.mediumFactors),
          factorCount
        ),
      },
      highFactorsToHighEnabled:
        srcThresholds.highFactorsToHighEnabled === undefined
          ? defaultThresholds.highFactorsToHighEnabled
          : srcThresholds.highFactorsToHighEnabled === true,
      highFactorsToHigh,
    },
  };
}

export function sanitizeRelationshipRiskSettings(raw) {
  const src = raw || {};
  const activeModel = normalizeRelationshipRiskModel(src.activeModel || src.model);
  const models = src.models || {};

  return {
    activeModel,
    models: {
      [RELATIONSHIP_RISK_MODEL_3_LEVEL]: sanitizeRelationshipRiskRules(
        models[RELATIONSHIP_RISK_MODEL_3_LEVEL] ||
          DEFAULT_RELATIONSHIP_RISK_RULES_BY_MODEL[RELATIONSHIP_RISK_MODEL_3_LEVEL],
        RELATIONSHIP_RISK_MODEL_3_LEVEL
      ),
      [RELATIONSHIP_RISK_MODEL_2_LEVEL]: sanitizeRelationshipRiskRules(
        models[RELATIONSHIP_RISK_MODEL_2_LEVEL] ||
          DEFAULT_RELATIONSHIP_RISK_RULES_BY_MODEL[RELATIONSHIP_RISK_MODEL_2_LEVEL],
        RELATIONSHIP_RISK_MODEL_2_LEVEL
      ),
    },
  };
}

export function relationshipRuleSummary(rules, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const counts = RELATIONSHIP_RISK_FACTOR_OPTIONS.reduce(
    (acc, option) => {
      const impact = normalizeRelationshipRiskImpactForModel(rules.factors?.[option.key]?.impact, model);
      acc[impact] = (acc[impact] || 0) + 1;
      return acc;
    },
    { immediate_high: 0, high: 0, immediate_medium: 0, medium: 0, none: 0 }
  );

  if (normalizeRelationshipRiskModel(model) === RELATIONSHIP_RISK_MODEL_2_LEVEL) {
    return `${counts.immediate_high} immediate high, ${counts.high} high factors, ${counts.medium} risk factors`;
  }

  return `${counts.immediate_high} immediate high, ${counts.high} high factors, ${counts.immediate_medium} immediate medium, ${counts.medium} medium factors`;
}

function activeFactorMap(inputs = {}) {
  const industryRisk = normalizeSourceRiskLevel(inputs.industryRisk);
  const geoRisk = normalizeSourceRiskLevel(inputs.geoRisk);

  return {
    industryHigh: industryRisk === "high",
    industryMedium: industryRisk === "medium",
    geoHigh: geoRisk === "high",
    geoMedium: geoRisk === "medium",
    pepExposure: inputs.pepExposure === true,
    sanctionsExposure: inputs.sanctionsExposure === true,
    negativeNews: inputs.negativeNews === true,
    complexStructure: inputs.complexStructure === true,
    notMetInPerson: inputs.notMetInPerson === true,
  };
}

export function calculateRelationshipRisk(inputs = {}, settings = DEFAULT_RELATIONSHIP_RISK_SETTINGS) {
  const cleanSettings = sanitizeRelationshipRiskSettings(settings);
  const model = cleanSettings.activeModel;
  const rules = cleanSettings.models[model];
  const activeFactors = activeFactorMap(inputs);
  const factorResults = [];

  RELATIONSHIP_RISK_FACTOR_OPTIONS.forEach((option) => {
    if (!activeFactors[option.key]) return;
    const impact = normalizeRelationshipRiskImpactForModel(rules.factors?.[option.key]?.impact, model);
    if (impact === "none") return;
    factorResults.push({
      key: option.key,
      label: FACTOR_REASON_LABELS[option.key] || option.label,
      impact,
    });
  });

  const immediateHigh = factorResults.filter((item) => item.impact === "immediate_high");
  const immediateMedium = factorResults.filter((item) => item.impact === "immediate_medium");
  const highFactors = factorResults.filter((item) => item.impact === "high");
  const mediumFactors = factorResults.filter((item) => item.impact === "medium");
  const riskAndHighFactors =
    model === RELATIONSHIP_RISK_MODEL_2_LEVEL
      ? [...mediumFactors, ...highFactors]
      : mediumFactors;
  const thresholds = rules.thresholds || {};
  const combined = thresholds.combinedFactorsToHigh || {};

  let level = RELATIONSHIP_RISK_LEVEL_STANDARD;
  let reasonSource = factorResults;

  if (immediateHigh.length) {
    level = RELATIONSHIP_RISK_LEVEL_HIGH;
    reasonSource = immediateHigh;
  } else if (thresholds.highFactorsToHighEnabled && highFactors.length >= (thresholds.highFactorsToHigh || 1)) {
    level = RELATIONSHIP_RISK_LEVEL_HIGH;
    reasonSource = highFactors;
  } else if (thresholds.mediumFactorsToHighEnabled && riskAndHighFactors.length >= (thresholds.mediumFactorsToHigh || 1)) {
    level = RELATIONSHIP_RISK_LEVEL_HIGH;
    reasonSource = riskAndHighFactors;
  } else if (
    combined.enabled &&
    highFactors.length >= (combined.highFactors || 1) &&
    mediumFactors.length >= (combined.mediumFactors || 1)
  ) {
    level = RELATIONSHIP_RISK_LEVEL_HIGH;
    reasonSource = [...highFactors, ...mediumFactors];
  } else if (
    model === RELATIONSHIP_RISK_MODEL_3_LEVEL &&
    (immediateMedium.length ||
      (thresholds.mediumFactorsToMediumEnabled &&
        mediumFactors.length >= (thresholds.mediumFactorsToMedium || 1)))
  ) {
    level = RELATIONSHIP_RISK_LEVEL_MEDIUM;
    reasonSource = immediateMedium.length ? immediateMedium : mediumFactors;
  }

  const reasons = reasonSource.length
    ? reasonSource.map((item) => item.label)
    : ["No configured final-risk triggers"];

  return {
    model,
    level,
    label: relationshipRiskLabel(level),
    reasons,
    factorResults,
    counts: {
      immediateHigh: immediateHigh.length,
      immediateMedium: immediateMedium.length,
      high: highFactors.length,
      medium: mediumFactors.length,
    },
    thresholds,
  };
}
